import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Divider, Chip, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TextField, Grid, Box, Tooltip,
} from '@mui/material';
import { AccountBalance } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dealerOrdersApi } from '../../../api/dealerOrders.api';
import { extractApiError } from '../../../api/error';
import { StatusBadge } from '../../../components/StatusBadge';
import { DealerSummaryCard } from '../../../components/DealerSummaryCard';
import { useAuth } from '../../../hooks/useAuth';

interface Props {
  orderId: number | null;
  onClose: () => void;
  onChanged: () => void;
}

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function OrderDetailDialog({ orderId, onClose, onChanged }: Props) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const open = orderId !== null;
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [transporter, setTransporter] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [payUtr, setPayUtr] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payAmount, setPayAmount] = useState('');

  const { data: order } = useQuery({
    queryKey: ['dealer-order', orderId],
    queryFn: () => dealerOrdersApi.get(orderId as number),
    enabled: open,
  });
  const { data: bank } = useQuery({ queryKey: ['order-payment-info'], queryFn: () => dealerOrdersApi.paymentInfo(), enabled: open });

  // Prefill the payment form from the order (defaults amount to the full grand total).
  useEffect(() => {
    if (!order) return;
    setPayUtr(order.paymentUtr ?? '');
    setPayDate(order.paymentDate ?? new Date().toISOString().slice(0, 10));
    setPayAmount(order.paymentAmount != null ? String(order.paymentAmount) : String(order.grandTotal));
  }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { setError(null); qc.invalidateQueries({ queryKey: ['dealer-order', orderId] }); onChanged(); };
  const onErr = (e: unknown) => setError(extractApiError(e));

  const submitMut = useMutation({ mutationFn: () => dealerOrdersApi.submit(orderId as number), onSuccess: refresh, onError: onErr });
  const savePaymentMut = useMutation({ mutationFn: () => dealerOrdersApi.savePayment(orderId as number, { utr: payUtr.trim(), paymentDate: payDate, amount: Number(payAmount) }), onSuccess: refresh, onError: onErr });
  const approveMut = useMutation({ mutationFn: () => dealerOrdersApi.approve(orderId as number), onSuccess: refresh, onError: onErr });
  const rejectMut = useMutation({ mutationFn: () => dealerOrdersApi.reject(orderId as number, rejectReason), onSuccess: refresh, onError: onErr });
  const startMfgMut = useMutation({ mutationFn: () => dealerOrdersApi.startManufacturing(orderId as number), onSuccess: refresh, onError: onErr });
  const completeMfgMut = useMutation({ mutationFn: () => dealerOrdersApi.completeManufacturing(orderId as number), onSuccess: refresh, onError: onErr });
  const allocateMut = useMutation({ mutationFn: () => dealerOrdersApi.allocate(orderId as number, Number(warehouseId)), onSuccess: refresh, onError: onErr });
  const dispatchMut = useMutation({ mutationFn: () => dealerOrdersApi.dispatch(orderId as number, { transporter: transporter || undefined, vehicleNo: vehicleNo || undefined }), onSuccess: refresh, onError: onErr });
  const deliverMut = useMutation({ mutationFn: (dispatchId: number) => dealerOrdersApi.deliver(orderId as number, dispatchId, { receivedBy }), onSuccess: refresh, onError: onErr });
  const confirmMut = useMutation({ mutationFn: (dispatchId: number) => dealerOrdersApi.confirmDelivery(orderId as number, dispatchId, { status: 'confirmed' }), onSuccess: refresh, onError: onErr });

  if (!order) return null;
  const s = order.status;
  const paid = order.paymentAmount != null;
  const editablePayment = (s === 'draft' || s === 'rejected') && hasPermission('dealer_order.submit');
  const amt = Number(payAmount || 0);
  const isFull = amt > 0 && Math.round(amt * 100) === Math.round(order.grandTotal * 100);
  const previewOutstanding = Math.max(0, order.grandTotal - amt);
  const payChip: { color: 'success' | 'warning' | 'default'; label: string } =
    order.paymentStatus === 'received' ? { color: 'success', label: 'Payment received' }
      : order.paymentStatus === 'advance' ? { color: 'warning', label: `Advance · Outstanding ${inr(order.outstandingAmount)}` }
        : { color: 'default', label: 'Payment pending' };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <span>{order.orderCode}</span>
          <StatusBadge status={order.status} />
          <Typography variant="body2" color="text.secondary">{inr(order.grandTotal)}</Typography>
          <Chip size="small" color={payChip.color} label={payChip.label} />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {order.rejectionReason && <Alert severity="warning" sx={{ mb: 2 }}>Rejected: {order.rejectionReason}</Alert>}

        <Box sx={{ mb: 2 }}><DealerSummaryCard dealerId={order.dealerId} /></Box>
        <Divider textAlign="left" sx={{ mb: 1 }}>Items</Divider>
        <Table size="small">
          <TableHead><TableRow><TableCell>Product</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Mfg</TableCell><TableCell align="right">Alloc</TableCell><TableCell align="right">Disp</TableCell><TableCell align="right">Deliv</TableCell><TableCell align="right">Total</TableCell></TableRow></TableHead>
          <TableBody>
            {(order.items ?? []).map((i) => (
              <TableRow key={i.id}>
                <TableCell>#{i.productId}</TableCell>
                <TableCell align="right">{i.quantity}</TableCell>
                <TableCell align="right">{i.manufacturedQty}</TableCell>
                <TableCell align="right">{i.allocatedQty}</TableCell>
                <TableCell align="right">{i.dispatchedQty}</TableCell>
                <TableCell align="right">{i.deliveredQty}</TableCell>
                <TableCell align="right">{inr(i.totalAmount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider textAlign="left" sx={{ my: 1 }}>Payment</Divider>
        {bank && (
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5, mb: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}><AccountBalance fontSize="small" color="primary" /><Typography variant="subtitle2">Remit to (NEFT / RTGS)</Typography></Stack>
            <Grid container spacing={0.5}>
              <Kv label="Account name" value={bank.accountName} />
              <Kv label="Bank" value={bank.bankName} />
              <Kv label="A/C no." value={bank.accountNumber} />
              <Kv label="IFSC" value={bank.ifscCode} />
              <Kv label="Bank address" value={bank.bankAddress} full />
            </Grid>
          </Box>
        )}
        {editablePayment ? (
          <>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="UTR / Ref No." value={payUtr} onChange={(e) => setPayUtr(e.target.value)} /></Grid>
              <Grid item xs={6} sm={4}><TextField size="small" fullWidth type="date" label="Payment date" InputLabelProps={{ shrink: true }} value={payDate} onChange={(e) => setPayDate(e.target.value)} /></Grid>
              <Grid item xs={6} sm={4}><TextField size="small" fullWidth type="number" label="Amount received (₹)" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} /></Grid>
            </Grid>
            <Stack direction="row" spacing={1.5} alignItems="center" mt={1} flexWrap="wrap" useFlexGap>
              {amt > 0 && (isFull
                ? <Chip size="small" color="success" label={`Full payment — ${inr(amt)}`} />
                : <Chip size="small" color="warning" label={`Advance ${inr(amt)} · Outstanding ${inr(previewOutstanding)}`} />)}
              <Button size="small" variant="outlined" disabled={!payUtr.trim() || !payDate || amt <= 0 || savePaymentMut.isPending} onClick={() => savePaymentMut.mutate()}>Save Payment</Button>
            </Stack>
            {!paid && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Save the payment details, then Submit for Approval.</Typography>}
          </>
        ) : paid ? (
          <Grid container spacing={0.5}>
            <Kv label="UTR / Ref" value={order.paymentUtr ?? '—'} />
            <Kv label="Paid on" value={order.paymentDate ?? '—'} />
            <Kv label="Amount received" value={inr(order.paymentAmount ?? 0)} />
            <Kv label="Outstanding" value={inr(order.outstandingAmount)} />
          </Grid>
        ) : <Typography variant="body2" color="text.secondary">No payment recorded.</Typography>}

        {(order.dispatches?.length ?? 0) > 0 && (
          <>
            <Divider textAlign="left" sx={{ my: 1 }}>Dispatches</Divider>
            <Table size="small">
              <TableHead><TableRow><TableCell>Code</TableCell><TableCell>Transporter</TableCell><TableCell>Vehicle</TableCell><TableCell>Status</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {(order.dispatches ?? []).map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.dispatchCode}</TableCell>
                    <TableCell>{d.transporter ?? '—'}</TableCell>
                    <TableCell>{d.vehicleNo ?? '—'}</TableCell>
                    <TableCell><Chip size="small" label={d.status} color={d.status === 'delivered' ? 'success' : 'default'} /></TableCell>
                    <TableCell align="right">
                      {d.status === 'dispatched' && hasPermission('dealer_dispatch.deliver') && (
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                          <TextField size="small" label="Received by" value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} />
                          <Button size="small" disabled={!receivedBy || deliverMut.isPending} onClick={() => deliverMut.mutate(d.id)}>Mark Delivered</Button>
                        </Stack>
                      )}
                      {d.status === 'delivered' && hasPermission('delivery_confirmation.create') &&
                        !(order.confirmations ?? []).some((c) => c.dispatchId === d.id) && (
                          <Button size="small" color="success" disabled={confirmMut.isPending} onClick={() => confirmMut.mutate(d.id)}>Confirm Receipt</Button>
                        )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <Divider textAlign="left" sx={{ my: 1 }}>Status History</Divider>
        <Table size="small">
          <TableBody>
            {(order.history ?? []).map((h) => (
              <TableRow key={h.id}>
                <TableCell>{h.action}</TableCell>
                <TableCell>{h.fromStatus ?? '—'} → {h.toStatus}</TableCell>
                <TableCell>{new Date(h.createdAt).toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {s === 'pending_approval' && hasPermission('dealer_order.approve') && (
          <Grid container spacing={1} mt={1}><Grid item xs={12}>
            <TextField size="small" label="Rejection reason (required to reject)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} fullWidth />
          </Grid></Grid>
        )}
        {s === 'manufactured' && hasPermission('dealer_order.allocate') && (
          <TextField size="small" label="Warehouse ID (for allocation)" type="number" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} sx={{ mt: 1 }} />
        )}
        {s === 'allocated' && hasPermission('dealer_dispatch.create') && (
          <Stack direction="row" spacing={2} mt={1}>
            <TextField size="small" label="Transporter" value={transporter} onChange={(e) => setTransporter(e.target.value)} />
            <TextField size="small" label="Vehicle No" value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {(s === 'draft' || s === 'rejected') && hasPermission('dealer_order.submit') && (
          <Tooltip title={!paid ? 'Add payment details first' : ''}>
            <span>
              <Button variant="contained" onClick={() => submitMut.mutate()} disabled={!paid || submitMut.isPending}>Submit for Approval</Button>
            </span>
          </Tooltip>
        )}
        {s === 'pending_approval' && hasPermission('dealer_order.approve') && (
          <>
            <Button color="success" variant="contained" onClick={() => approveMut.mutate()} disabled={approveMut.isPending}>Approve</Button>
            <Button color="error" variant="outlined" disabled={!rejectReason || rejectMut.isPending} onClick={() => rejectMut.mutate()}>Reject</Button>
          </>
        )}
        {s === 'approved' && hasPermission('manufacturing.manage') && (
          <Button variant="contained" onClick={() => startMfgMut.mutate()} disabled={startMfgMut.isPending}>Start Manufacturing</Button>
        )}
        {s === 'in_manufacturing' && hasPermission('manufacturing.manage') && (
          <Button variant="contained" onClick={() => completeMfgMut.mutate()} disabled={completeMfgMut.isPending}>Complete Manufacturing</Button>
        )}
        {s === 'manufactured' && hasPermission('dealer_order.allocate') && (
          <Button variant="contained" disabled={!warehouseId || allocateMut.isPending} onClick={() => allocateMut.mutate()}>Allocate Stock</Button>
        )}
        {s === 'allocated' && hasPermission('dealer_dispatch.create') && (
          <Button variant="contained" onClick={() => dispatchMut.mutate()} disabled={dispatchMut.isPending}>Dispatch</Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function Kv({ label, value, full }: { label: string; value: React.ReactNode; full?: boolean }) {
  return (
    <Grid item xs={full ? 12 : 6} sm={full ? 12 : 4}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Grid>
  );
}
